import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  NotFoundPage,
  // ShowcasePage,
  DeliveriesPage,
  PackagesPage,
  ShipmentsPage,
  InvoicesPage,
  DeveloperPage,
  WAREHOUSE_PATHS,
  WarehouseStockPage,
  WarehouseSkuPage,
  WarehouseOutboundListPage,
  WarehouseOutboundDetailPage,
  WarehouseInboundListPage,
  WarehouseInboundDetailPage,
  WarehousePurchaseOrderListPage,
  WarehousePurchaseOrderDetailPage,
} from '@/pages';
import { RouteConfig } from '@/types';
import {
  APP_CONFIG,
  hasDeveloperAccess,
  isDeveloperPageEnabled,
} from '@/config/app';
import ClientGate from '@/router/ClientGate';

const page = (
  path: string,
  element: React.ReactElement,
  title: string
): RouteConfig => ({ path, element, title });

export const routes: RouteConfig[] = [
  {
    path: '/shipments',
    element: <ShipmentsPage />,
    title: 'Shipments',
    icon: 'shipments',
  },
  {
    path: '/deliveries',
    element: <DeliveriesPage />,
    title: 'Deliveries',
    icon: 'deliveries',
  },
  {
    path: '/packages',
    element: <PackagesPage />,
    title: 'Packages',
    icon: 'packages',
  },
  {
    path: '/invoices',
    element: <InvoicesPage />,
    title: 'Invoices',
    icon: 'invoices',
  },
  // Warehouse (read-only WMS). Each page is gated per client by the backend's
  // WMS access (ClientGate) — same as the sidebar group.
  page(WAREHOUSE_PATHS.stock, <WarehouseStockPage />, 'Stock'),
  page(WAREHOUSE_PATHS.sku, <WarehouseSkuPage />, 'SKU'),
  page(
    WAREHOUSE_PATHS.outbound,
    <WarehouseOutboundListPage />,
    'Outbound request'
  ),
  page(
    `${WAREHOUSE_PATHS.outbound}/:id`,
    <WarehouseOutboundDetailPage />,
    'Outbound request'
  ),
  page(
    WAREHOUSE_PATHS.inbound,
    <WarehouseInboundListPage />,
    'Inbound request'
  ),
  page(
    `${WAREHOUSE_PATHS.inbound}/:id`,
    <WarehouseInboundDetailPage />,
    'Inbound request'
  ),
  page(
    WAREHOUSE_PATHS.purchaseOrder,
    <WarehousePurchaseOrderListPage />,
    'Purchase order'
  ),
  page(
    `${WAREHOUSE_PATHS.purchaseOrder}/:id`,
    <WarehousePurchaseOrderDetailPage />,
    'Purchase order'
  ),
  {
    path: '/warehouse',
    element: <Navigate to={WAREHOUSE_PATHS.stock} replace />,
    title: 'Warehouse',
  },
  // Behind the isDeveloperPageEnabled kill switch — when off there is no
  // /developer route at all (falls through to NotFound), matching the hidden
  // sidebar entry. When on, only clients allowed an API (shipment or WMS
  // outbound order, see hasDeveloperAccess) get past the gate.
  ...(!isDeveloperPageEnabled()
    ? []
    : [
        {
          path: '/developer',
          element: (
            <ClientGate allowed={hasDeveloperAccess}>
              <DeveloperPage />
            </ClientGate>
          ),
          title: 'Developer',
          icon: 'developer',
        },
      ]),
  // {
  //   path: '/showcase',
  //   element: <ShowcasePage />,
  //   title: 'DS Showcase',
  //   icon: 'palette',
  // },
  {
    path: '/',
    element: <Navigate to={APP_CONFIG.homeRoute} replace />,
    title: 'Home',
  },
  {
    path: '*',
    element: <NotFoundPage />,
    title: 'Page Not Found',
  },
];

export const getPageTitle = (pathname: string): string => {
  const route = routes.find((route) => route.path === pathname);
  return route?.title || APP_CONFIG.name;
};
